/**
 * One step of a visitor's way from landing on the desk to an answer.
 *
 * Henry Liner, 2026-09-17: "it would be good to know that 100 people visited but only 5
 * queries". The server side is routes/journey.ts and the table is migration 083; this is the
 * browser's half, and it has one rule above every other: NOTHING HERE MAY BREAK A PAGE. Every
 * call is fire-and-forget, every failure is swallowed, and no caller branches on the result -
 * the same contract recordAeContactClick in api.ts keeps, for the same reason.
 */

export type JourneyKind =
  | 'visit' | 'form_start' | 'search_blocked' | 'search' | 'code_requested' | 'verified' | 'answer';

const STORAGE_KEY = 'lh_vid';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** A v4 uuid, with a fallback for the rare browser that has getRandomValues but not randomUUID. */
function newId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** The fallback when storage is refused - a private window, blocked site data. */
let pageId: string | null = null;

/**
 * This browser's id: random, made once, kept in localStorage.
 *
 * Derived from nothing about the person - see 083. When storage throws, the id lives for this
 * page load only, which makes that browser a new visitor on every load. That overcounts, and
 * an overcount is the safe direction: it can only make the visit-to-answer rate look worse.
 */
export function visitorId(): string {
  try {
    const kept = localStorage.getItem(STORAGE_KEY);
    if (kept && UUID.test(kept)) return kept;
    const made = pageId ?? newId();
    pageId = made;
    localStorage.setItem(STORAGE_KEY, made);
    return made;
  } catch {
    return (pageId ??= newId());
  }
}

/** `fields` is for 'search_blocked' only: the keys of the boxes the form refused. */
export async function recordStep(kind: JourneyKind, fields?: readonly string[]): Promise<void> {
  try {
    const body: Record<string, string | null | readonly string[]> = { visitorId: visitorId(), kind };
    if (kind === 'search_blocked' && fields?.length) body['fields'] = fields.slice(0, 20);
    if (kind === 'visit') {
      // Raw, and reduced on the server: the host is kept, the path never is.
      body['referrer'] = document.referrer || null;
      const q = new URLSearchParams(window.location.search);
      body['utmSource'] = q.get('utm_source');
      body['utmCampaign'] = q.get('utm_campaign');
    }
    await fetch('/api/journey', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
      // Lets the last step of a page - "answer" as the broker closes the tab - still leave.
      keepalive: true,
    });
  } catch {
    // Offline, blocked, a relative URL in a test runner: all the same non-event to a broker.
  }
}

let visited = false;

/**
 * The visit, once per page load.
 *
 * Guarded here rather than by the caller's effect, because React runs a mount effect twice
 * under StrictMode in development, and a guard in the component would be one more thing a
 * later edit could move.
 */
export function recordVisit(): void {
  if (visited) return;
  visited = true;
  void recordStep('visit');
}
